import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listDeals,
  listCustomers,
  createDeal,
  updateDeal,
  deleteDeal,
  type DealStage,
  type PipelineDeal,
} from "@/lib/api";
import { STAGE_COLUMNS, STAGE_COLOR, fmtMoney } from "@/lib/crm";
import { Kanban } from "@/components/Kanban";
import { Modal } from "@/components/Modal";

export function PipelinePage() {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [openDeal, setOpenDeal] = useState<PipelineDeal | null>(null);

  const dealsKey = ["deals"] as const;
  const { data: deals } = useQuery({ queryKey: dealsKey, queryFn: listDeals });

  const move = useMutation({
    mutationFn: (vars: { id: string; stage: DealStage }) =>
      updateDeal(vars.id, { stage: vars.stage }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: dealsKey });
      const prev = qc.getQueryData<PipelineDeal[]>(dealsKey);
      qc.setQueryData<PipelineDeal[]>(dealsKey, (old) =>
        old?.map((d) => (d.id === vars.id ? { ...d, stage: vars.stage } : d)),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(dealsKey, ctx.prev),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: dealsKey });
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });

  const open = deals?.filter((d) => d.stage !== "won" && d.stage !== "lost") ?? [];
  const openValue = open.reduce((s, d) => s + (d.value ?? 0), 0);

  return (
    <div>
      <header className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="page-title">Pipeline</h1>
          <p className="mt-2 text-text-secondary">
            {open.length
              ? `${open.length} open deal${open.length === 1 ? "" : "s"} · ${fmtMoney(openValue)}`
              : "Drag deals through the funnel as they progress."}
          </p>
        </div>
        <button
          onClick={() => setAddOpen(true)}
          className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
          style={{ color: "var(--color-on-accent)" }}
        >
          <i className="fa-solid fa-plus mr-1.5 text-xs" />
          New deal
        </button>
      </header>

      <Kanban
        columns={STAGE_COLUMNS.map((c) => ({
          key: c.stage,
          label: c.label,
          color: STAGE_COLOR[c.stage],
        }))}
        items={deals ?? []}
        idOf={(d) => d.id}
        columnOf={(d) => d.stage}
        onMove={(id, stage) => move.mutate({ id, stage })}
        onCardClick={setOpenDeal}
        sortItems={(a, b) => (b.value ?? 0) - (a.value ?? 0)}
        columnFooter={(items) => {
          const total = items.reduce((s, d) => s + (d.value ?? 0), 0);
          return total ? <span>{fmtMoney(total)}</span> : null;
        }}
        renderCard={(d) => (
          <>
            <p className="text-sm font-medium leading-snug text-text-heading">{d.title}</p>
            <div className="mt-3 flex items-center justify-between">
              <span className="truncate text-[11px] text-text-muted">{d.customerName}</span>
              {d.value != null && (
                <span className="ml-2 shrink-0 text-[11px] font-semibold text-text-secondary">
                  {fmtMoney(d.value)}
                </span>
              )}
            </div>
          </>
        )}
      />

      {addOpen && <NewDealModal onClose={() => setAddOpen(false)} />}
      {openDeal && (
        <DealDrawer
          deal={openDeal}
          onClose={() => setOpenDeal(null)}
        />
      )}
    </div>
  );
}

function NewDealModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { data: customers } = useQuery({ queryKey: ["customers"], queryFn: listCustomers });
  const [customerId, setCustomerId] = useState("");
  const [title, setTitle] = useState("");
  const [value, setValue] = useState("");
  const [stage, setStage] = useState<DealStage>("lead");

  const create = useMutation({
    mutationFn: () =>
      createDeal({
        customerId,
        title,
        value: value ? Number(value) : undefined,
        stage,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
      onClose();
    },
  });

  const inputCls =
    "mb-3 w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";

  return (
    <Modal open onClose={onClose} title="New deal">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <label className="mb-1 block text-xs font-medium text-text-muted">Customer</label>
        <select
          className={inputCls}
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          required
        >
          <option value="" disabled>
            Choose a customer…
          </option>
          {customers?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        {customers && customers.length === 0 && (
          <p className="-mt-2 mb-3 text-xs text-text-muted">
            No customers yet — add one on the Customers page first.
          </p>
        )}

        <label className="mb-1 block text-xs font-medium text-text-muted">Title</label>
        <input
          className={inputCls}
          value={title}
          placeholder="e.g. Product launch video series"
          onChange={(e) => setTitle(e.target.value)}
          required
        />

        <div className="flex gap-3">
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-text-muted">Value (£)</label>
            <input
              className={inputCls}
              type="number"
              min="0"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-text-muted">Stage</label>
            <select
              className={inputCls}
              value={stage}
              onChange={(e) => setStage(e.target.value as DealStage)}
            >
              {STAGE_COLUMNS.map((s) => (
                <option key={s.stage} value={s.stage}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-1 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-token-md border border-border px-4 py-2 text-sm text-text-secondary hover:text-text-heading"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={create.isPending || !customerId}
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ color: "var(--color-on-accent)" }}
          >
            {create.isPending ? "Creating…" : "Create deal"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function DealDrawer({ deal, onClose }: { deal: PipelineDeal; onClose: () => void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(deal.title);
  const [value, setValue] = useState(deal.value?.toString() ?? "");
  const [stage, setStage] = useState<DealStage>(deal.stage);
  const [notes, setNotes] = useState(deal.notes ?? "");
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setTitle(deal.title);
    setValue(deal.value?.toString() ?? "");
    setStage(deal.stage);
    setNotes(deal.notes ?? "");
    setDirty(false);
  }, [deal]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["deals"] });
    qc.invalidateQueries({ queryKey: ["customers"] });
    qc.invalidateQueries({ queryKey: ["customer", deal.customerId] });
  };

  const save = useMutation({
    mutationFn: () =>
      updateDeal(deal.id, {
        title,
        value: value ? Number(value) : null,
        stage,
        notes: notes || null,
      }),
    onSuccess: () => {
      invalidate();
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteDeal(deal.id),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const mark = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setDirty(true);
  };

  const inputCls =
    "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";
  const labelCls = "mb-1 block text-xs font-medium text-text-muted";

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-xl flex-col overflow-y-auto rounded-token-lg border border-border bg-bg-elevated shadow-token-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <span className="flex items-center gap-2 text-sm text-text-muted">
            <span
              className="h-2 w-2 rounded-full"
              style={{ background: STAGE_COLOR[stage] }}
            />
            {deal.customerName}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (confirm("Delete this deal? This cannot be undone.")) remove.mutate();
              }}
              className="rounded-md px-2 py-1 text-xs text-danger hover:bg-danger/10"
            >
              <i className="fa-solid fa-trash mr-1" />
              Delete
            </button>
            <button
              onClick={onClose}
              className="grid h-7 w-7 place-items-center rounded-md text-text-muted hover:bg-bg-card-hover hover:text-text-heading"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        </div>

        <div className="flex-1 px-5 py-4">
          <input
            className="mb-4 w-full bg-transparent font-display text-xl font-semibold text-text-heading outline-none"
            value={title}
            onChange={(e) => mark(setTitle)(e.target.value)}
          />

          <div className="mb-4 flex gap-3">
            <div className="flex-1">
              <label className={labelCls}>Stage</label>
              <select
                className={inputCls}
                value={stage}
                onChange={(e) => mark(setStage)(e.target.value as DealStage)}
              >
                {STAGE_COLUMNS.map((s) => (
                  <option key={s.stage} value={s.stage}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className={labelCls}>Value (£)</label>
              <input
                className={inputCls}
                type="number"
                min="0"
                value={value}
                onChange={(e) => mark(setValue)(e.target.value)}
              />
            </div>
          </div>

          <label className={labelCls}>Notes</label>
          <textarea
            className={`${inputCls} mb-4`}
            rows={5}
            placeholder="Next steps, blockers, context…"
            value={notes}
            onChange={(e) => mark(setNotes)(e.target.value)}
          />
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-border px-5 py-3">
          {saved && <span className="text-xs text-accent">Saved</span>}
          <button
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ color: "var(--color-on-accent)" }}
          >
            {save.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
